import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { Gavel, Mail, Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

// reCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = '6Lc-PT4sAAAAAKM4fhxuYXk_rgYA56weRL2Hzgoz';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);
  
  const { login } = useAuth();
  const { config } = useSiteConfig();

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaVerified(true);
    } else {
      setCaptchaVerified(false);
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // reCAPTCHA kontrolü - test ortamı için geçici olarak devre dışı
    // if (!captchaVerified) {
    //   setError('Lütfen "Ben robot değilim" doğrulamasını tamamlayın');
    //   return;
    // }

    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('E-posta veya şifre hatalı');
      } else if (err.code === 'auth/user-not-found') {
        setError('Bu e-posta ile kayıtlı kullanıcı bulunamadı');
      } else if (err.code === 'auth/invalid-email') {
        setError('Geçersiz e-posta adresi');
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
      // Reset captcha on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaVerified(false);
    }
    
    setLoading(false);
  };

  const primaryColor = config.primaryColor || '#f59e0b';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden"
        style={{
          background: config.loginImage 
            ? `url(${config.loginImage}) center/cover no-repeat`
            : `linear-gradient(135deg, ${primaryColor}22 0%, #1e293b 50%, #0f172a 100%)`
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-transparent" />
        
        {/* Decorative Elements */}
        {!config.loginImage && (
          <>
            <div 
              className="absolute top-20 left-20 w-72 h-72 rounded-full blur-3xl opacity-30"
              style={{ backgroundColor: primaryColor }}
            />
            <div 
              className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl opacity-20"
              style={{ backgroundColor: primaryColor }}
            />
          </>
        )}
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-12 xl:p-16">
          <div className="max-w-lg">
            {config.logoLight ? (
              /* Logo varsa sadece logo göster */
              <img src={config.logoLight} alt="Logo" className="h-20 mb-8 object-contain" />
            ) : (
              /* Logo yoksa ikon ve metin göster */
              <>
                <div className="flex items-center gap-3 mb-8">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Gavel className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                  {config.siteTitle || 'BERKAY ÇAKIR'}
                </h1>
                <p className="text-xl text-white/80 mb-8">
                  {config.siteSubtitle || 'Hukuk Bürosu'}
                </p>
              </>
            )}
            <div className="flex items-center gap-4 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span>Güvenli Bağlantı</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-white/40" />
              <span>İcra Takip Sistemi</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            {config.logoDark ? (
              /* Logo varsa sadece logo göster */
              <img src={config.logoDark} alt="Logo" className="h-14 mx-auto mb-4 object-contain" />
            ) : (
              /* Logo yoksa ikon ve metin göster */
              <>
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl"
                  style={{ 
                    backgroundColor: primaryColor,
                    boxShadow: `0 20px 40px ${primaryColor}40`
                  }}
                >
                  <Gavel className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
                  {config.siteTitle || 'BERKAY ÇAKIR'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 text-sm tracking-widest uppercase mt-1">
                  {config.siteSubtitle || 'Hukuk Bürosu'}
                </p>
              </>
            )}
          </div>

          {/* Form Card */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl shadow-slate-200/50 dark:shadow-black/20 p-8 sm:p-10 border border-slate-100 dark:border-slate-700">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
                Hoş Geldiniz
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mt-2">
                Devam etmek için giriş yapın
              </p>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl mb-6 flex items-center gap-3 text-sm animate-shake">
                <AlertCircle size={18} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  E-posta Adresi
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-600 dark:group-focus-within:text-slate-300 transition-colors" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-slate-700/50 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-700 transition-all text-slate-800 dark:text-white placeholder-slate-400"
                    placeholder="ornek@email.com"
                    required
                    data-testid="email-input"
                    style={{ '--tw-border-opacity': 1, borderColor: 'var(--primary-color)' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Şifre
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-600 dark:group-focus-within:text-slate-300 transition-colors" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 bg-slate-50 dark:bg-slate-700/50 border-2 border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:border-primary focus:bg-white dark:focus:bg-slate-700 transition-all text-slate-800 dark:text-white placeholder-slate-400"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* reCAPTCHA - Test ortamı için devre dışı */}
              {/* 
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  theme="light"
                  hl="tr"
                />
              </div>
              */}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
                style={{ 
                  backgroundColor: primaryColor,
                  boxShadow: `0 10px 30px ${primaryColor}50`
                }}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>Giriş yapılıyor...</span>
                  </>
                ) : (
                  <span>Giriş Yap</span>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-slate-400 dark:text-slate-500 text-xs mt-8">
            AMC Fikir Sanat © {new Date().getFullYear()} | İcra Takip Sistemi
          </p>
        </div>
      </div>
    </div>
  );
}
